import type { FunctionComponent } from 'react';
import {
  EuiFormFieldset,
  EuiSpacer,
  EuiText,
  EuiIcon,
  EuiPanel,
  EuiSkeletonText,
} from '@elastic/eui';
import Intro from './intro';
import QuickActions from '@components/quick-actions';
import AuthnSection from '@components/authn-section';
import GroupSection from '@components/group-section';
import GeoroleSection from '@components/georole-section';
import { usePerson } from '@data/person';

interface PersonViewProps {
  idnumber: string;
}

const PersonView: FunctionComponent<PersonViewProps> = ({ idnumber }) => {
  const { person, loading } = usePerson(idnumber);

  if (loading || person == null) {
    return <EuiSkeletonText lines={3} />;
  }

  const suspended = person.authn?.suspended ?? false;
  return (
    <>
      <EuiFormFieldset>
        {suspended && (
          <EuiPanel color="danger" paddingSize="m">
            <EuiText size="m" color="danger">
              <h2>
                <EuiIcon type="warning" size="l" /> Account Suspended
              </h2>
              <p>
                This user is suspended and access rights may not be granted to
                them. Please contact the Data & Development team if the
                suspension should be lifted.
              </p>
            </EuiText>
          </EuiPanel>
        )}
        <EuiSpacer size="s" />
        <Intro person={person} />
      </EuiFormFieldset>
      <EuiSpacer size="m" />

      <EuiFormFieldset legend={{ children: 'Quick actions' }}>
        <QuickActions person={person} />
      </EuiFormFieldset>
      <EuiSpacer size="m" />

      <EuiFormFieldset legend={{ children: 'Authentication details' }}>
        <AuthnSection person={person} />
      </EuiFormFieldset>
      <EuiSpacer size="m" />

      <EuiFormFieldset legend={{ children: 'Groups' }}>
        {suspended && (
          <EuiText size="m" color="danger">
            <p>
              This user is suspended and access rights may not be granted to
              them.
            </p>
          </EuiText>
        )}
        <GroupSection person={person} />
      </EuiFormFieldset>
      <EuiSpacer size="m" />

      <EuiFormFieldset legend={{ children: 'Georoles' }}>
        {suspended && (
          <EuiText size="m" color="danger">
            <p>
              This user is suspended and access rights may not be granted to
              them.
            </p>
          </EuiText>
        )}
        <GeoroleSection person={person} />
      </EuiFormFieldset>
    </>
  );
};

export default PersonView;
